import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';

interface SolarPVInstallersCostData {
  chartData: any;
  chartOptions: any;
  narrative?: string;
  totalPairs?: number;
  topPair?: string;
  topPairCost?: number;
  avgCost?: number;
}

// Helper function to get geofips for a location
async function getGeofipsForLocation(locationName: string): Promise<number | null> {
  try {
    return await supabaseLocationService.getGeofips(locationName);
  } catch (error) {
    console.error('Error getting geofips for location:', locationName, error);
    return null;
  }
}

export default async function fetchSolarPVInstallersCostSupabase(location: string): Promise<SolarPVInstallersCostData> {
  try {
    // Get geofips for the location
    const geofips = await getGeofipsForLocation(location);
    
    if (!geofips) {
      // Return placeholder data if no geofips found
      return {
        chartData: {
          labels: [],
          datasets: [{
            label: 'Cost per Watt',
            data: [],
            backgroundColor: '#3b82f6',
            borderColor: '#3b82f6',
            borderWidth: 1
          }]
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Top Solar PV Installer-Manufacturer Pairs (Cost) - ${location} (No Geofips Found)`,
              font: { size: 16, weight: 'bold' as const }
            },
            legend: {
              display: false
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Installer-Manufacturer Pair'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Cost per Watt ($/W)'
              },
              beginAtZero: true
            }
          }
        },
        narrative: `No geofips found for ${location} in the population database. This location may not be available in the solar installation database.`,
        totalPairs: 0,
        topPair: 'N/A',
        topPairCost: 0,
        avgCost: 0
      };
    }

    // Query Supabase by geoid for server-side filtering
    let { data: installerData, error } = await supabaseClient
      .from('pv_top10_installers_by_location')
      .select('*')
      .eq('geoid', geofips)
      .order('total_kw', { ascending: false })
      .limit(10);

    if (error) {
      console.error('Error fetching Solar PV Installers data:', error);
      throw error;
    }

    // Fallback: if no rows by geoid, try location_name (case-insensitive)
    if (!installerData || installerData.length === 0) {
      const { data: byLocation, error: locErr } = await supabaseClient
        .from('pv_top10_installers_by_location')
        .select('*')
        .ilike('location_name', `%${location}%`)
        .order('total_kw', { ascending: false })
        .limit(10);
      if (locErr) {
        console.error('Error fetching Solar PV Installers by location_name:', locErr);
      } else if (byLocation && byLocation.length > 0) {
        installerData = byLocation;
      }
    }

    if (!installerData || installerData.length === 0) {
      // Return placeholder data if no records found
      return {
        chartData: {
          labels: [],
          datasets: [{
            label: 'Cost per Watt',
            data: [],
            backgroundColor: '#3b82f6',
            borderColor: '#3b82f6',
            borderWidth: 1
          }]
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Top Solar PV Installer-Manufacturer Pairs (Cost) - ${location} (No Data Available)`,
              font: { size: 16, weight: 'bold' as const }
            },
            legend: {
              display: false
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Installer-Manufacturer Pair'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Cost per Watt ($/W)'
              },
              beginAtZero: true
            }
          }
        },
        narrative: `No solar PV installer data available for ${location}. This may indicate that the location has not yet been added to the solar installation database, or that solar installations are not yet recorded for this area.`,
        totalPairs: 0,
        topPair: 'N/A',
        topPairCost: 0,
        avgCost: 0
      };
    }

    // Process the data to create chart datasets
    // Calculate cost per watt and filter out invalid entries
    const processedData = installerData
      .map(item => {
        const totalKw = Number(item.total_kw) || 0;
        const totalCost = Number(item.total_cost) || 0;
        const costPerWatt = totalKw > 0 && totalCost > 0 ? totalCost / (totalKw * 1000) : 0; // Convert kW to W
        
        return {
          installer: item.installer || 'Unknown',
          manufacturer: item.manufacturer || 'Unknown',
          totalKw: totalKw,
          totalCost: totalCost,
          totalProjects: Number(item.total_projects) || 0,
          costPerWatt: costPerWatt
        };
      })
      .filter(item => item.costPerWatt > 0 && item.totalKw > 0)
      .sort((a, b) => b.totalKw - a.totalKw) // Sort by capacity first
      .slice(0, 10);

    // Combine installer and manufacturer: "Installer (Manufacturer)"
    const labels = processedData.map(item => {
      const installer = item.installer || 'Unknown';
      const manufacturer = item.manufacturer || 'Unknown';
      return manufacturer !== 'Unknown' && manufacturer !== installer 
        ? `${installer} (${manufacturer})`
        : installer;
    });
    const data = processedData.map(item => item.costPerWatt);

    // Create color array for bars
    const colors = [
      'rgba(59, 130, 246, 0.8)',   // Blue
      'rgba(16, 185, 129, 0.8)',   // Green
      'rgba(245, 158, 11, 0.8)',   // Amber
      'rgba(239, 68, 68, 0.8)',    // Red
      'rgba(139, 92, 246, 0.8)',   // Purple
      'rgba(6, 182, 212, 0.8)',    // Cyan
      'rgba(251, 146, 60, 0.8)',   // Orange
      'rgba(34, 197, 94, 0.8)',    // Green
      'rgba(168, 85, 247, 0.8)',   // Purple
      'rgba(236, 72, 153, 0.8)'    // Pink
    ];

    const chartData = {
      labels: labels,
      datasets: [{
        label: 'Cost per Watt',
        data: data,
        backgroundColor: colors.slice(0, labels.length),
        borderColor: colors.slice(0, labels.length).map(c => c.replace('0.8', '1')),
        borderWidth: 1
      }]
    };

    const chartOptions = {
      type: 'bar' as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: `Top Solar PV Installer-Manufacturer Pairs (Cost) - ${location}`,
          font: { size: 18, weight: 'bold' as const },
          padding: { top: 10, bottom: 20 }
        },
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context: any) {
              const index = context.dataIndex;
              const item = processedData[index];
              return [
                `Installer: ${item.installer}`,
                `Manufacturer: ${item.manufacturer}`,
                `Cost per Watt: $${item.costPerWatt.toFixed(2)}/W`,
                `Total kW: ${item.totalKw.toFixed(1)} kW`,
                `Total Cost: $${item.totalCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
                `Total Projects: ${item.totalProjects.toLocaleString()}`
              ].filter(Boolean);
            }
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Installer-Manufacturer Pair',
            font: { size: 14, weight: 'bold' as const }
          },
          ticks: {
            maxRotation: 45,
            minRotation: 45,
            font: { size: 12 }
          }
        },
        y: {
          title: {
            display: true,
            text: 'Cost per Watt ($/W)',
            font: { size: 14, weight: 'bold' as const }
          },
          ticks: {
            font: { size: 12 }
          },
          beginAtZero: true
        }
      }
    };

    // Calculate summary metrics
    const totalPairs = processedData.length;
    const topPair = processedData.length > 0 
      ? (processedData[0].manufacturer !== 'Unknown' && processedData[0].manufacturer !== processedData[0].installer
          ? `${processedData[0].installer} (${processedData[0].manufacturer})`
          : processedData[0].installer)
      : 'N/A';
    const topPairCost = processedData.length > 0 ? processedData[0].costPerWatt : 0;
    const avgCost = processedData.length > 0
      ? processedData.reduce((sum, item) => sum + item.costPerWatt, 0) / processedData.length
      : 0;

    const narrative = `${location} has ${totalPairs} top solar PV installer-manufacturer pairs with cost data. The leading pair is ${topPair} with a cost of $${topPairCost.toFixed(2)} per watt. The average cost per watt across all top pairs is $${avgCost.toFixed(2)}.`;

    return {
      chartData,
      chartOptions,
      narrative,
      totalPairs,
      topPair,
      topPairCost,
      avgCost
    };

  } catch (error) {
    console.error('Error in fetchSolarPVInstallersCostSupabase:', error);
    throw error;
  }
}

